import { HeroSection } from "@/components/landing/HeroSection";
import { ToolsOverview } from "@/components/landing/ToolsOverview";
// import { TrustStatement } from "@/components/landing/TrustStatement";
import { LandingFaqSection } from "@/components/landing/LandingFaqSection";
import { ComparisonSection } from "@/components/landing/ComparisonSection";
// import { HowItWorksSection } from "@/components/landing/HowItWorksSection";
import { SEOContentSection } from "@/components/landing/SEOContentSection";
import { InternalLinkSection } from "@/components/seo/InternalLinkSection";
import { TOOL_SEO_ROUTES } from "@/config/toolSeoRegistry";
import { useSEO } from "@/hooks/useSEO";
import { DEFAULT_OG_IMAGE, SITE_NAME, buildSiteSearchUrl } from "@/config/siteSeo";
import { buildCollectionPageSchema } from "@/lib/seo/schema";

const mapLink = (route: (typeof TOOL_SEO_ROUTES)[number]) => ({
  href: route.path,
  label: route.name,
  description: route.description,
});

const homepageLinkGroups = {
  organize: TOOL_SEO_ROUTES.filter((route) =>
    [
      "/tools/merge",
      "/tools/split",
      "/tools/remove-pages",
      "/tools/edit-pdf",
      "/tools/rearrange",
      "/tools/page-extractor",
    ].includes(route.path),
  ).map(mapLink),
  convert: TOOL_SEO_ROUTES.filter((route) =>
    [
      "/tools/convert/pdf-to-word",
      "/tools/word-to-pdf",
      "/tools/pdf-to-excel",
      "/tools/pdf-to-pptx",
      "/tools/html-to-pdf",
      "/tools/excel-to-pdf",
    ].includes(route.path),
  ).map(mapLink),
  secure: TOOL_SEO_ROUTES.filter((route) =>
    [
      "/tools/compress",
      "/tools/pdf-security-audit",
      "/tools/pdf-sanitizer",
      "/tools/encrypt",
      "/tools/pdf-signer",
      "/batch-tools/security-audit",
    ].includes(route.path),
  ).map(mapLink),
  ai: TOOL_SEO_ROUTES.filter((route) => route.family === "ai")
    .slice(0, 6)
    .map(mapLink),
};

const Index = () => {
  useSEO({
    title: "PDFWorkSpace | Free Online PDF Workspace & PDF Tools",
    description:
      "PDFWorkSpace is a free online PDF workspace for merging, splitting, editing, OCR, signing, and securing PDFs. Work privately in your browser with no uploads or account required.",
    keywords:
      "privacy-first pdf tools, client-side pdf tools, no upload pdf tools, merge pdf, split pdf, compress pdf, edit pdf",
    canonicalPath: "/",
    ogType: "website",
    ogTitle: "Privacy-First PDF Tools for Everyday Document Work",
    ogDescription:
      "Merge, split, compress, convert, and secure PDFs with browser-based workflows built for privacy and fast document work.",
    ogImage: DEFAULT_OG_IMAGE,
    twitterTitle: "Privacy-First PDF Tools | Merge, Split, Compress PDFs",
    twitterDescription:
      "Run PDF workflows in your browser with no upload-first architecture. Merge, split, compress, convert, and secure documents locally.",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: "https://pdfwork.space/",
        potentialAction: {
          "@type": "SearchAction",
          target: buildSiteSearchUrl(),
          "query-input": "required name=search_term_string",
        },
        description:
          "A privacy-first suite of PDF tools that runs entirely in the browser.",
      },
      buildCollectionPageSchema({
        title: "PDFWorkSpace Homepage",
        path: "/",
        description:
          "Privacy-first PDF tools for editing, converting, organizing, and securing documents in the browser.",
      }),
    ],
  });

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      {/* Global Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Animated Gradient Orbs */}
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px] animate-pulse opacity-40 mix-blend-screen" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px] animate-pulse delay-1000 opacity-30 mix-blend-screen" />

        {/* Grid Pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      <div className="relative z-10">
        <HeroSection />
        <ToolsOverview />
        <section className="mx-auto max-w-6xl space-y-6 px-4 py-8 md:py-10">
          <InternalLinkSection
            title="Organize PDF Files"
            description="Start here for high-intent organizing tasks like merging, splitting, removing pages, and editing document structure."
            links={homepageLinkGroups.organize}
          />
          <InternalLinkSection
            title="Convert PDF and Office Files"
            description="These routes target conversion searches such as PDF to Word, Word to PDF, PDF to Excel, and presentation workflows."
            links={homepageLinkGroups.convert}
            compact
          />
          <InternalLinkSection
            title="Secure and Optimize PDF Documents"
            description="These routes cover file size reduction, encryption, security review, sanitization, and signed-document workflows."
            links={homepageLinkGroups.secure}
            compact
          />
          <InternalLinkSection
            title="Study and Analyze PDFs with AI"
            description="These pages cover chat, summary, quiz, flashcards, and study workflows built around PDF content."
            links={homepageLinkGroups.ai}
            compact
          />
          <InternalLinkSection
            title="Popular PDF How-To Guides"
            description="Step-by-step guides for common PDF tasks across devices, industries, and use cases."
            links={[
              { href: "/how-to/compress-pdf-for-email", label: "Compress PDF for Email", description: "Shrink PDFs to fit email attachment limits." },
              { href: "/how-to/merge-pdf-on-iphone", label: "Merge PDF on iPhone", description: "Combine PDFs directly in your mobile browser." },
              { href: "/how-to/ocr-pdf-in-hindi", label: "OCR PDF in Hindi", description: "Extract Hindi text from scanned PDFs." },
              { href: "/how-to/split-pdf-for-lawyers", label: "Split PDF for Lawyers", description: "Extract case sections from legal bundles." },
              { href: "/how-to/sign-pdf-for-business", label: "Sign PDF for Business", description: "eSign contracts without leaving the browser." },
              { href: "/how-to/edit-pdf-for-students", label: "Edit PDF for Students", description: "Annotate study materials directly in browser." },
            ]}
            compact
          />
          <InternalLinkSection
            title="PDF Tools by Audience"
            description="Explore curated landing pages for students, small businesses, and legal professionals using PDFWorkSpace."
            links={[
              { href: "/students", label: "Free PDF Tools for Students", description: "Summaries, quizzes, flashcards, and study-ready PDF workflows." },
              { href: "/business", label: "Free PDF Tools for Small Business", description: "eSign, redact, merge, encrypt, and compress documents for work." },
              { href: "/legal", label: "Free PDF Tools for Legal Professionals", description: "Redaction, OCR, encryption, and filing workflows for legal teams." },
            ]}
            compact
          />
        </section>
        {/* <HowItWorksSection /> */}
        {/* <TrustStatement /> */}
        <SEOContentSection />
        <ComparisonSection />
        <LandingFaqSection />
      </div>
    </div>
  );
};

export default Index;
